import { AllowedEvent } from '@fhs-legacy/frontend/src/state/crm-events/types';

import { PaymentOrchestrator, PaymentPipelineError, PaymentPipelineResult } from '../../../payment';
import { isPaymentCancelled } from '../../../payment/payment-cancelled-codes';
import { usePaymentFlowStore } from '../../../state/payment-stores/payment-flow-store';

import { logCommitRetryReprice, logUserActivation } from './commit-retry-reprice-debug';
import {
  clearDigitalWalletDetailsFromContext,
  getDefaultRetryStep,
  getDigitalWalletRetryStep,
  isCommitFailureRetry,
  isDigitalWalletPaymentError,
  isRetryablePaymentError,
  needsFreshPipelineContextOnRetry,
} from './payment-retry-utils';

interface HandlePlaceOrderDependencies {
  lastResultRef: React.MutableRefObject<PaymentPipelineResult | null>;
  orchestratorRef: React.MutableRefObject<PaymentOrchestrator>;
  buildPipelineContext: () => any;
  setShouldSkipPriceOrder: (skip: boolean) => void;
  setIsProcessing: (processing: boolean) => void;
  setCurrentError: (error: PaymentPipelineError | null) => void;
  logRBIEvent: (event: AllowedEvent) => void;
}

// Shared across the Place Order button and gift-card reload sheet so a second entry cannot
// start another commit while one is already running.
let isPlaceOrderInFlight = false;

/** @returns false when another place-order is already running; true once this call owns the lock. */
export async function handlePlaceOrder({
  lastResultRef,
  orchestratorRef,
  setCurrentError,
  setIsProcessing,
  setShouldSkipPriceOrder,
  buildPipelineContext,
  logRBIEvent,
}: HandlePlaceOrderDependencies): Promise<boolean> {
  if (isPlaceOrderInFlight) {
    logCommitRetryReprice('handle_place_order_blocked_in_flight');
    return false;
  }
  isPlaceOrderInFlight = true;

  const {
    retryInfo: existingRetryInfo,
    setRetryInfo,
    setCurrentState,
    setIsCommitRetry,
  } = usePaymentFlowStore.getState();
  const lastResult = lastResultRef.current;
  const isRetry = !!lastResult?.error?.retryFromStep;
  const isCommitRetryAttempt =
    isRetry &&
    (lastResult?.error?.retryFromStep === 'CommitOrderStep' ||
      isCommitFailureRetry(lastResult?.error?.code));
  let orderSubmitted = false;
  let failureErrorCode: string | undefined;

  if (isCommitRetryAttempt) {
    setIsCommitRetry(true);
  }

  logCommitRetryReprice('handle_place_order_enter', {
    isRetry,
    isCommitRetryAttempt,
    lastErrorCode: lastResult?.error?.code ?? null,
    paymentMethod: lastResult?.context?.paymentMethod ?? null,
  });
  logUserActivation('handle_place_order_enter');

  orchestratorRef.current.setCRMLogger(logRBIEvent);

  try {
    if (!isRetry && !getPipelineContext(buildPipelineContext, setCurrentError)) {
      logCommitRetryReprice('handle_place_order_exit', { reason: 'no_retry_no_context' });
      return true;
    }

    setCurrentError(null);
    setIsProcessing(true);

    const needsFreshContext =
      isRetry && needsFreshPipelineContextOnRetry(lastResult.error?.code);

    let result: PaymentPipelineResult;
    if (
      isRetry &&
      !needsFreshContext &&
      lastResult != null &&
      lastResult.context != null &&
      lastResult.error != null &&
      lastResult.error.retryFromStep != null
    ) {
      setRetryInfo(null);
      setCurrentState('idle');
      const retryContext = clearDigitalWalletDetailsFromContext(lastResult.context);
      const retryFromStep = getDigitalWalletRetryStep(
        retryContext.paymentMethod,
        lastResult.error.retryFromStep
      );
      logCommitRetryReprice('handle_place_order_execute', {
        path: 'skip_to_step',
        skipToStep: retryFromStep,
        rbiOrderId: retryContext.rbiOrderId,
      });
      result = await orchestratorRef.current.execute(retryContext, {
        skipToStep: retryFromStep,
      });
    } else {
      const pipelineContext = getPipelineContext(buildPipelineContext, setCurrentError);
      if (!pipelineContext) {
        if (isRetry && existingRetryInfo) {
          setRetryInfo(existingRetryInfo);
        }
        logCommitRetryReprice('handle_place_order_exit', {
          reason: 'context_not_ready',
          isRetry,
          needsFreshContext,
        });
        return true;
      }
      setRetryInfo(null);
      setCurrentState('idle');
      const freshContext = clearDigitalWalletDetailsFromContext(pipelineContext);
      logCommitRetryReprice('handle_place_order_execute', {
        path: needsFreshContext ? 'full_pipeline_fresh_context' : 'full_pipeline',
        rbiOrderId: freshContext.rbiOrderId,
        cartTotal: freshContext.cartTotal,
        paymentMethod: freshContext.paymentMethod,
        needsFreshContext,
      });
      logUserActivation('before_pipeline_execute');
      result = await orchestratorRef.current.execute(freshContext);
    }

    lastResultRef.current = result;

    logCommitRetryReprice('handle_place_order_result', {
      success: result.success,
      failedStep: result.failedStep ?? null,
      errorCode: result.error?.code ?? null,
    });

    if (result.success) {
      orderSubmitted = true;
      handleSuccess(setShouldSkipPriceOrder, setRetryInfo, setCurrentError);
      return true;
    }

    failureErrorCode = result.error?.code;
    handleError(result, setCurrentError, setRetryInfo);
    if (!isPaymentCancelled(result.error?.code)) {
      setCurrentState('error');
    }

    if (
      isDigitalWalletPaymentError(result.error?.code) ||
      isPaymentCancelled(result.error?.code) ||
      result.error?.code === 'GOOGLE_PAY_USER_ACTIVATION_REQUIRED'
    ) {
      lastResultRef.current = null;
    }
  } catch (error) {
    failureErrorCode = 'UNEXPECTED_ERROR';
    handleUnexpectedError(error, setCurrentError, setRetryInfo);
    setCurrentState('error');
  } finally {
    isPlaceOrderInFlight = false;
    usePaymentFlowStore.getState().setIsCommitRetry(false);
    if (!orderSubmitted) {
      // Hold the loading state through background pre-reprice so Retry does not flash enabled.
      if (!isCommitFailureRetry(failureErrorCode)) {
        setIsProcessing(false);
      }
    }
  }

  return true;
}

function getPipelineContext(
  buildPipelineContext: HandlePlaceOrderDependencies['buildPipelineContext'],
  setCurrentError: HandlePlaceOrderDependencies['setCurrentError']
) {
  const pipelineContext = buildPipelineContext();

  if (!pipelineContext) {
    setCurrentError({
      code: 'CONTEXT_NOT_READY',
      message: 'Pipeline context is not ready',
      userMessage:
        'Unable to process payment. Please check your payment details and try again. Error Code: (Pipeline Context Readiness)',
    });
    return null;
  }

  return pipelineContext;
}

function handleSuccess(
  setShouldSkipPriceOrder: (skip: boolean) => void,
  setRetryInfo: (info: any) => void,
  setCurrentError: (error: PaymentPipelineError | null) => void
) {

  setShouldSkipPriceOrder(true);
  setRetryInfo(null);
  setCurrentError(null);
}

function handleError(
  result: PaymentPipelineResult,
  setCurrentError: (error: PaymentPipelineError | null) => void,
  setRetryInfo: (info: any) => void
) {
  const errorCode = result.error?.code;

  if (isPaymentCancelled(errorCode) || errorCode === 'GOOGLE_PAY_USER_ACTIVATION_REQUIRED') {
    setCurrentError(null);
    if (errorCode === 'GOOGLE_PAY_USER_ACTIVATION_REQUIRED') {
      return;
    }
    setRetryInfo(null);
    return;
  }

  setCurrentError(result.error ?? null);

  if (isDigitalWalletPaymentError(errorCode)) {
    return;
  }

  const retryFromStep = result.error?.retryFromStep ?? getDefaultRetryStep(errorCode);

  if (retryFromStep && isRetryablePaymentError(errorCode)) {
    setRetryInfo({
      canRetry: true,
      retryFromStep,
      errorCode: result.error?.code ?? 'UNKNOWN_ERROR',
      errorMessage: result.error?.message ?? 'Unknown payment error',
      failedStep: result.failedStep,
    });
  }
}

function handleUnexpectedError(
  error: unknown,
  setCurrentError: (error: PaymentPipelineError | null) => void,
  setRetryInfo: (info: any) => void
) {
  setCurrentError({
    code: 'UNEXPECTED_ERROR',
    message: error instanceof Error ? error.message : 'Unknown error',
    userMessage:
      'An unexpected error occurred. Please try again. Error Code: (Unexpected Orchestrator Error)',
  });
  setRetryInfo(null);
}
